import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import { supabase } from "../../lib/supabase";
import { AUDIO_TYPES } from "../../data/audioTypes";
import { useAuth } from "../../contexts/AuthContext";
import { notifyChange } from "../notifications/notify";

const MAX_FILE_SIZE_MB = 50;

export function validateAudioFile(file) {
  if (!file.type.startsWith("audio/")) {
    return { valid: false, error: "File must be an audio format (mp3, wav, m4a, etc.)" };
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return { valid: false, error: `File must be under ${MAX_FILE_SIZE_MB}MB` };
  }
  return { valid: true };
}

// Formats a freshly-saved creative as markdown for the CSM notification.
// Audio Types degrade gracefully on shape drift (objects with display_name
// → name → value); when the resolved list is empty (= "All Types") we say
// so explicitly because the UI label and the persisted [] differ. New
// creatives always start `Paused` (status_id 3) per the insert payload.
function formatNewCreativeMarkdown({ name, source, tags, audioTypes, startDate, endDate, duration }) {
  const lines = [];
  lines.push(`**Name:** ${name && name.trim() ? name.trim() : "(unnamed)"}`);
  lines.push(`**Source:** ${source === "upload" ? "Audio upload" : "Script"}`);
  if (Array.isArray(tags) && tags.length) {
    lines.push(`**Tags:** ${tags.join(", ")}`);
  }
  const audioLabel = !Array.isArray(audioTypes) || audioTypes.length === 0
    ? "All Types"
    : audioTypes.map(a => {
        if (a && typeof a === "object") {
          const label = a.display_name || a.name || a.value || "Unknown";
          return a.clusivity === "exclude" ? `¬${label}` : label;
        }
        return String(a);
      }).join(", ");
  lines.push(`**Audio Types:** ${audioLabel}`);
  if (startDate) lines.push(`**Start date:** ${startDate}`);
  if (endDate) lines.push(`**End date:** ${endDate}`);
  if (duration != null) lines.push(`**Duration:** ${duration}s`);
  lines.push(`**Status:** Paused`);
  return lines.join("\n");
}

export function NewCreativeModal({ advertiserId, canCreate = true, onClose, onCreated }) {
  const { isSupportUser } = useAuth();
  const [mode, setMode] = useState("script");
  const [script, setScript] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [name, setName] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [duration, setDuration] = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [audioTypes, setAudioTypes] = useState([]);
  const [audioOpen, setAudioOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const inputStyle = { width:"100%", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:10, padding:"10px 14px", color:"#151515", fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };

  const toggleAudio = (type) => {
    setAudioTypes(prev =>
      prev.some(t => t.value === type.value)
        ? prev.filter(t => t.value !== type.value)
        : [...prev, { ...type, clusivity:"include" }]
    );
  };

  const toggleClusivity = (e, type) => {
    e.stopPropagation();
    setAudioTypes(prev =>
      prev.map(t => t.value === type.value ? { ...t, clusivity: t.clusivity === "include" ? "exclude" : "include" } : t)
    );
  };

  const audioLabel = audioTypes.length === 0
    ? "All Types"
    : audioTypes.map(t => `${t.clusivity === "exclude" ? "¬" : ""}${t.display_name}`).join(", ");

  const switchToUpload = () => { setMode("upload"); setSaveError(null); fileInputRef.current?.click(); };
  const switchToScript = () => { setMode("script"); setSelectedFile(null); setSaveError(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setMode("script"); return; }
    const validation = validateAudioFile(file);
    if (!validation.valid) { setSaveError(validation.error); setMode("script"); e.target.value = ""; return; }
    setSaveError(null);
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    const audio = new window.Audio(url);
    audio.addEventListener("loadedmetadata", () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(Math.round(audio.duration));
      URL.revokeObjectURL(url);
    });
  };

  const addTag = (value) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) setTags(prev => [...prev, trimmed]);
    setTagInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
    else if (e.key === "Backspace" && tagInput === "" && tags.length > 0) setTags(prev => prev.slice(0, -1));
  };

  const removeTag = (index) => setTags(prev => prev.filter((_, i) => i !== index));

  const resolvedAudioTypes = audioTypes.length === AUDIO_TYPES.length && audioTypes.every(t => t.clusivity === "include") ? [] : audioTypes;
  const dateError = startDate && endDate && endDate < startDate ? "End date must be after start date." : null;

  const handleLaunch = async () => {
    if (!canCreate) {
      setSaveError("You have read-only access for this advertiser.");
      return;
    }
    if (!supabase || advertiserId == null) { setSaveError("Not signed in or no advertiser."); return; }
    setSaveError(null);
    setSaving(true);

    if (mode === "script") {
      const wordCount = script.trim().split(/\s+/).filter(Boolean).length;
      const estimatedDuration = Math.max(15, Math.round((wordCount / 130) * 60));
      const { error } = await supabase.from("creatives").insert({
        advertiser_id: advertiserId, script: script.trim(), name: name.trim() || null,
        duration: estimatedDuration, labels: tags, status_id: 3,
        start_date: startDate || null, end_date: endDate || null, audio_types: resolvedAudioTypes,
      });
      setSaving(false);
      if (error) { setSaveError(error.message); return; }
      // Fire-and-forget CSM notification. Skipped for admins so internal
      // QA / setup creatives don't page the CSM. Errors are swallowed inside
      // notifyChange so a Clarify/Zapier outage never blocks the modal close.
      if (!isSupportUser) {
        notifyChange({
          supabase,
          eventType: "Creative Gallery - New Creative",
          advertiserId,
          previousState: "",
          newState: formatNewCreativeMarkdown({
            name, source: "script", tags, audioTypes: resolvedAudioTypes,
            startDate, endDate, duration: estimatedDuration,
          }),
        });
      }
      onCreated?.();
      onClose();
      return;
    }

    const { data: insertData, error: insertError } = await supabase
      .from("creatives")
      .insert({
        advertiser_id: advertiserId, script: null, name: name.trim() || null,
        duration: duration, labels: tags, status_id: 3,
        start_date: startDate || null, end_date: endDate || null, audio_types: resolvedAudioTypes,
      })
      .select("id")
      .single();

    if (insertError) { setSaving(false); setSaveError(insertError.message); return; }

    const creativeId = insertData.id;
    const storagePath = `${advertiserId}/${creativeId}/${Date.now()}-${selectedFile.name}`;

    const { error: uploadError } = await supabase.storage
      .from("creative-uploads")
      .upload(storagePath, selectedFile, { contentType: selectedFile.type });

    if (uploadError) {
      await supabase.from("creatives").delete().eq("id", creativeId);
      setSaving(false);
      setSaveError("Audio upload failed. Please try again.");
      return;
    }

    const { data: urlData } = supabase.storage.from("creative-uploads").getPublicUrl(storagePath);

    const { error: updateError } = await supabase
      .from("creatives")
      .update({ audio_files: { url: urlData.publicUrl, filename: selectedFile.name, size_bytes: selectedFile.size } })
      .eq("id", creativeId);

    if (updateError) {
      await supabase.storage.from("creative-uploads").remove([storagePath]);
      await supabase.from("creatives").delete().eq("id", creativeId);
      setSaving(false);
      setSaveError("Failed to save upload reference. Please try again.");
      return;
    }

    setSaving(false);
    // Fire-and-forget CSM notification. See script branch for rationale.
    // Fired AFTER the audio_files update so the row is fully persisted; if
    // anything earlier rolled the insert back, we never reach this point.
    if (!isSupportUser) {
      notifyChange({
        supabase,
        eventType: "Creative Gallery - New Creative",
        advertiserId,
        previousState: "",
        newState: formatNewCreativeMarkdown({
          name, source: "upload", tags, audioTypes: resolvedAudioTypes,
          startDate, endDate, duration,
        }),
      });
    }
    onCreated?.();
    onClose();
  };

  const isSubmitDisabled = !canCreate || saving || !!dateError || (mode === "script" ? script.trim().length < 10 : !selectedFile);

  return ReactDOM.createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:20, width:"100%", maxWidth:560, maxHeight:"90vh", overflowY:"auto", position:"relative", boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>

        <div style={{ padding:"28px 28px 20px", borderBottom:"1px solid #F0F0F0" }}>
          <button onClick={onClose} style={{ position:"absolute", top:16, right:20, background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
          <div style={{ fontSize:11, color:"#7b7b7b", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>New Creative</div>
          <h2 style={{ fontSize:20, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>{mode === "upload" ? "Upload your audio" : "Add a script"}</h2>
        </div>

        <div style={{ padding:"24px 28px" }}>

          {mode === "script" ? (
            <>
              <div style={{ fontSize:13, color:"#626262", marginBottom:10 }}>
                Paste your script below, or{" "}
                <button onClick={switchToUpload} style={{ background:"none", border:"none", color:"#F90091", fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline", fontFamily:"inherit" }}>
                  upload your audio file
                </button>.
              </div>
              <textarea
                value={script} onChange={e => setScript(e.target.value)}
                placeholder="Paste your ad script here..."
                style={{ width:"100%", minHeight:160, background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:12, padding:16, color:"#151515", fontSize:13, lineHeight:1.6, resize:"vertical", outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
              />
              <div style={{ marginTop:8, marginBottom:16 }}>
                <span style={{ fontSize:11, color: script.split(/\s+/).filter(Boolean).length > 80 ? "#e53935" : "#7b7b7b" }}>
                  {script.split(/\s+/).filter(Boolean).length} words
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize:13, color:"#626262", marginBottom:10 }}>
                Upload your audio file, or{" "}
                <button onClick={switchToScript} style={{ background:"none", border:"none", color:"#F90091", fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline", fontFamily:"inherit" }}>
                  use a script instead
                </button>.
              </div>
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{ border:"2px dashed #E2E2E2", borderRadius:12, padding:"28px 16px", textAlign:"center", cursor:"pointer", marginBottom:16, background: selectedFile ? "#FFF5FB" : "#FAFAFA" }}
              >
                {selectedFile ? (
                  <div>
                    <div style={{ fontSize:13, color:"#F90091", fontWeight:600, marginBottom:4 }}>{selectedFile.name}</div>
                    <div style={{ fontSize:11, color:"#7b7b7b" }}>{(selectedFile.size / (1024*1024)).toFixed(2)} MB · {duration}s detected</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize:13, color:"#7b7b7b", marginBottom:4 }}>Click to select audio file</div>
                    <div style={{ fontSize:11, color:"#B8B8B8" }}>MP3, WAV, M4A — max 50MB</div>
                  </div>
                )}
              </div>
            </>
          )}

          <input ref={fileInputRef} type="file" accept="audio/*" onChange={handleFileChange} style={{ display:"none" }} />

          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Variant A — Emotional" style={inputStyle} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Tags (optional)</label>
            <div style={{ ...inputStyle, display:"flex", flexWrap:"wrap", gap:6, alignItems:"center", cursor:"text", minHeight:40, padding:"6px 10px" }} onClick={() => document.getElementById("tag-input")?.focus()}>
              {tags.map((t, i) => (
                <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, padding:"2px 8px", background:"#EEF2FF", color:"#323084", borderRadius:4 }}>
                  {t}
                  <button onClick={() => removeTag(i)} style={{ background:"none", border:"none", color:"#323084", cursor:"pointer", padding:0, fontSize:12, lineHeight:1 }}>×</button>
                </span>
              ))}
              <input
                id="tag-input" type="text" value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => tagInput.trim() && addTag(tagInput)}
                placeholder={tags.length === 0 ? "e.g. Variant A" : ""}
                style={{ flex:1, minWidth:80, background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:13, fontFamily:"inherit", padding:0 }}
              />
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom: dateError ? 8 : 16 }}>
            <div>
              <label style={{ display:"block", fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Start date (optional)</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ display:"block", fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>End date (optional)</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inputStyle} />
            </div>
          </div>
          {dateError && <div style={{ fontSize:12, color:"#e53935", marginBottom:16 }}>{dateError}</div>}

          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <label style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1 }}>Inventory Types</label>
              <button onClick={() => setAudioOpen(o => !o)} style={{ fontSize:12, color:"#F90091", background:"none", border:"none", cursor:"pointer", padding:0 }}>
                {audioOpen ? "Done" : "Edit"}
              </button>
            </div>
            <div style={{ fontSize:13, color:"#151515", fontWeight:500, marginBottom: audioOpen ? 10 : 0 }}>{audioLabel}</div>
            {audioOpen && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:8 }}>
                {AUDIO_TYPES.map(type => {
                  const active = audioTypes.find(t => t.value === type.value);
                  const isExclude = active?.clusivity === "exclude";
                  const borderColor = active ? (isExclude ? "#e53935" : "#F90091") : "#E2E2E2";
                  return (
                    <div key={type.value} onClick={() => toggleAudio(type)}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#FAFAFA", borderRadius:8, cursor:"pointer", border:`1px solid ${borderColor}` }}>
                      <div style={{ width:16, height:16, borderRadius:4, background: active ? (isExclude ? "#e53935" : "#F90091") : "#F0F0F0", border:`1px solid ${active ? (isExclude ? "#e53935" : "#F90091") : "#CFCFCF"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {active && <span style={{ color:"#fff", fontSize:10, lineHeight:1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize:13, color: active ? "#151515" : "#626262", flex:1 }}>{type.display_name}</span>
                      {active && (
                        <button
                          onClick={(e) => toggleClusivity(e, active)}
                          style={{ fontSize:10, padding:"2px 8px", borderRadius:4, border:`1px solid ${isExclude ? "#e53935" : "#F90091"}`, background: isExclude ? "#FFF5F5" : "#FFF5FB", color: isExclude ? "#e53935" : "#F90091", cursor:"pointer", fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}
                        >
                          {isExclude ? "exclude" : "include"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {saveError && <div style={{ marginBottom:12, fontSize:12, color:"#e53935" }}>{saveError}</div>}
          <button
            onClick={handleLaunch}
            disabled={isSubmitDisabled}
            style={{ width:"100%", padding:"13px 0", background: isSubmitDisabled ? "#E2E2E2" : "#F90091", color: isSubmitDisabled ? "#B8B8B8" : "#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor: isSubmitDisabled ? "default" : "pointer" }}>
            {saving ? "Saving…" : mode === "upload" ? "Upload & Activate" : "Launch Creative"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
